import React, { memo, useMemo } from 'react'
import { Alert, Progress, Steps, Typography } from 'antd'
import {
  CheckOutlined,
  CloudSyncOutlined,
  DisconnectOutlined,
  ExclamationOutlined,
} from '@ant-design/icons'
import { useIntl } from 'react-intl'
import styles from './style.module.scss'

const STEP_KEYS = ['products', 'price_rules']

const PERCENT_BY_STEP = { products: 35, price_rules: 80, done: 100 }

/**
 * Tampilan progres sinkronisasi katalog lokal: ikon hero beranimasi, judul, langkah
 * (Produk → Promo & harga → Selesai), progress bar, dan pesan error.
 */
const CatalogSyncProgress = ({ status, step, errorType, errorDetail }) => {
  const { formatMessage } = useIntl()
  const t = (id, values) => formatMessage({ id }, values)

  const isError = status === 'error'
  const isSuccess = status === 'success'
  const currentIndex = step === 'done' ? STEP_KEYS.length : Math.max(STEP_KEYS.indexOf(step), 0)

  const items = useMemo(
    () =>
      [...STEP_KEYS, 'done'].map((key, index) => {
        let stepStatus = 'wait'
        if (index < currentIndex || (isSuccess && key === 'done')) {
          stepStatus = 'finish'
        } else if (index === currentIndex) {
          stepStatus = isError ? 'error' : 'process'
        }
        return {
          key,
          title: formatMessage({ id: `pos.catalog_sync.step.${key}` }),
          status: stepStatus,
        }
      }),
    [currentIndex, formatMessage, isError, isSuccess],
  )

  let heroClass = `${styles.heroIcon} ${styles.running}`
  let HeroIcon = CloudSyncOutlined
  if (isError) {
    heroClass = `${styles.heroIcon} ${styles.error}`
    HeroIcon = errorType === 'offline' ? DisconnectOutlined : ExclamationOutlined
  } else if (isSuccess) {
    heroClass = `${styles.heroIcon} ${styles.success}`
    HeroIcon = CheckOutlined
  }

  let statusText = t(`pos.catalog_sync.fetching.${step}`)
  if (isSuccess) {
    statusText = t('pos.catalog_sync.success')
  }

  return (
    <div>
      <div className={heroClass} aria-hidden="true">
        <HeroIcon />
      </div>
      <Typography.Title level={3} className={styles.title}>
        {isSuccess ? t('pos.catalog_sync.success') : t('pos.catalog_sync.title')}
      </Typography.Title>
      <p className={styles.subtitle}>{t('pos.catalog_sync.description')}</p>

      <Steps className={styles.steps} size="small" items={items} responsive={false} />

      <Progress
        className={styles.progress}
        percent={isSuccess ? 100 : (PERCENT_BY_STEP[step] ?? 10)}
        status={isError ? 'exception' : isSuccess ? 'success' : 'active'}
        showInfo={false}
        strokeColor={isError || isSuccess ? undefined : { from: '#4678f3', to: '#47cdf3' }}
      />
      {!isError && <div className={styles.statusText}>{statusText}</div>}

      {isError && (
        <Alert
          style={{ marginTop: 16, textAlign: 'left' }}
          type="error"
          showIcon
          message={t(
            errorType === 'offline'
              ? 'pos.catalog_sync.error_offline'
              : 'pos.catalog_sync.error_generic',
          )}
          description={errorDetail || undefined}
        />
      )}
    </div>
  )
}

export default memo(CatalogSyncProgress)
